import { Fragment } from 'react';

import { Collapse, Stack } from '@mui/material';
import cx from 'classnames';

import { MenuListItem } from '../MenuListItem';
import styles from './MenuList.module.scss';
import { useMenuItemActive } from '../../hooks/useMenuItemActive';
import type { MenuItem } from '../MenuListItem/types';
import { usePortfolioDefaultLink } from '@/hooks/filters';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useSidebarStore } from '@/stores/sidebar';

interface MenuListProps {
  menuItems: MenuItem[];
  isChildList?: boolean;
  parentExpanded?: boolean;
}

export const MenuList = ({
  menuItems,
  isChildList = false,
  parentExpanded = false,
}: MenuListProps) => {
  const {
    currentExpandedParentMenuItem,
    currentHighlightedParentMenuItem,
    handleToggleExpandedMenuItem,
  } = useSidebarStore();

  const isMobile = useIsMobile();
  const portfolioDefaultLink = usePortfolioDefaultLink();

  const handleMenuItemClick = (id: string) => () => {
    handleToggleExpandedMenuItem(id);
  };

  // Make to value based on wether it has children or not
  // and if it is customer portal url
  const toValue = (item: MenuItem, isExpandable: boolean) => {
    if (isExpandable) {
      return null;
    }

    // Use dynamic link for Portfolio to include default view filters
    if (item.id === 'ProjectPortfolioOverview') {
      return portfolioDefaultLink;
    }

    return item.path;
  };

  const { isMenuItemActive } = useMenuItemActive();

  return (
    <Stack
      className={cx(styles.MenuList, {
        [styles.MenuListChildList]: isChildList,
      })}
      direction="column"
      useFlexGap={false}
      spacing={isChildList ? 0.8 : 1.4}
      gap={0}
    >
      {menuItems.map((item) => {
        const isExpandable = (item.items && item.items?.length > 0) || false;
        const isCurrentlyExpanded = currentExpandedParentMenuItem === item.id;

        return (
          <Fragment key={`menu-item-${item.label}`}>
            <MenuListItem
              label={item.label}
              iconName={item.icon}
              status={item.status}
              to={toValue(item, isExpandable)}
              isActive={isMenuItemActive(item)}
              isActiveMenuListParent={
                currentHighlightedParentMenuItem === item.id
              }
              isExpandable={isExpandable}
              isExpanded={isCurrentlyExpanded}
              isChildItem={isChildList}
              onClick={handleMenuItemClick(item.id)}
              tabIndex={isChildList && !parentExpanded ? -1 : 0}
              target={item.openInNewTab ? '_blank' : undefined}
              id={item.id}
            />

            {isExpandable && item.items ? (
              <Collapse
                id={`collapsible-menu-${item.label.toLowerCase()}${isMobile ? '-mobile' : ''}`}
                in={isCurrentlyExpanded}
                collapsedSize={0}
                aria-hidden={!isCurrentlyExpanded}
              >
                <MenuList
                  menuItems={item.items}
                  isChildList
                  parentExpanded={isCurrentlyExpanded}
                />
              </Collapse>
            ) : null}
          </Fragment>
        );
      })}
    </Stack>
  );
};
